import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiMinus,
  FiPlus,
  FiShoppingCart,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  clearCart,
  getCart,
  getLocalCart,
  removeCartItem,
  saveLocalCart,
} from '../../lib/sqlAuth';
import { hideHomeBannerAd, resumeHomeBannerAd } from '../../lib/admobService';
import '../../csscomponent/CartDrawer.css';

export default function CartDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => getLocalCart());
  const [loading, setLoading] = useState(false);

  const cartItems = cart?.items || [];
  const totalAmount =
    cart?.totalInr ||
    cartItems.reduce(
      (sum, item) => sum + (Number(item.priceInr) || 0) * (Number(item.quantity) || 1),
      0
    );

  const loadCartData = () => {
    getCart()
      .then((res) => {
        setCart({
          items: Array.isArray(res?.items) ? res.items : [],
          totalInr: Number(res?.totalInr) || 0,
        });
      })
      .catch(() => {
        setCart(getLocalCart());
      });
  };

  useEffect(() => {
    if (open) {
      loadCartData();
      hideHomeBannerAd();
      document.body.classList.add('cart-drawer-open');
    } else {
      document.body.classList.remove('cart-drawer-open');
      resumeHomeBannerAd();
    }

    return () => {
      document.body.classList.remove('cart-drawer-open');
    };
  }, [open]);

  useEffect(() => {
    const handleCartUpdate = () => {
      loadCartData();
    };
    window.addEventListener('sd:cart-updated', handleCartUpdate);
    return () => window.removeEventListener('sd:cart-updated', handleCartUpdate);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleUpdateQuantity = async (item, delta) => {
    if (loading) return;
    setLoading(true);
    try {
      const currentQty = Number(item.quantity) || 1;
      const nextQty = currentQty + delta;

      if (nextQty <= 0) {
        const res = await removeCartItem({ itemId: item.id || item.packageId });
        setCart(res || getLocalCart());
        toast.info(`${item.packageName || 'Item'} removed from cart.`);
      } else {
        const current = getLocalCart();
        const nextItems = current.items.map((i) =>
          i.id === item.id ? { ...i, quantity: nextQty } : i
        );
        const res = saveLocalCart({ items: nextItems });
        setCart(res);
      }
    } catch (_) {
      toast.error('Unable to update cart item.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (item) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await removeCartItem({ itemId: item.id || item.packageId });
      setCart(res || getLocalCart());
      toast.info(`${item.packageName || 'Item'} removed from cart.`);
    } catch (_) {
      toast.error('Unable to remove item from cart.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (loading || cartItems.length === 0) return;
    setLoading(true);
    try {
      await clearCart();
      setCart({ items: [], totalInr: 0 });
      toast.info('Cart cleared.');
    } catch (_) {
      toast.error('Unable to clear cart.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = () => {
    onClose();
    navigate('/dashboard/cart');
  };

  const handleBrowse = (path) => {
    onClose();
    navigate(path);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="cart-drawer-backdrop"
        onClick={onClose}
        aria-label="Close cart drawer"
      />
      <aside
        className="cart-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping Cart"
      >
        <div className="cart-drawer-header">
          <div className="cart-drawer-title-wrap">
            <FiShoppingCart className="cart-drawer-title-icon" aria-hidden="true" />
            <h2 className="cart-drawer-title">Shopping Cart</h2>
            <span className="cart-drawer-count-badge">
              {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
            </span>
          </div>
          <button
            type="button"
            className="cart-drawer-close-btn"
            onClick={onClose}
            aria-label="Close cart"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div className="cart-drawer-body">
          {cartItems.length === 0 ? (
            <div className="cart-drawer-empty">
              <div className="cart-drawer-empty-icon-wrap">
                <FiShoppingCart aria-hidden="true" />
              </div>
              <h3>Your cart is empty</h3>
              <p>Explore our modern web and digital product packages to get started.</p>
              <div className="cart-drawer-empty-actions">
                <button
                  type="button"
                  className="cart-drawer-explore-btn primary"
                  onClick={() => handleBrowse('/services')}
                >
                  Browse Services
                </button>
                <button
                  type="button"
                  className="cart-drawer-explore-btn secondary"
                  onClick={() => handleBrowse('/product')}
                >
                  Browse Products
                </button>
              </div>
            </div>
          ) : (
            <div className="cart-drawer-items-list">
              {cartItems.map((item) => {
                const itemTotal = (Number(item.priceInr) || 0) * (Number(item.quantity) || 1);
                return (
                  <article key={item.id || item.packageId} className="cart-drawer-item-card">
                    <div className="cart-drawer-item-info">
                      <h4 className="cart-drawer-item-name">{item.packageName || 'Package'}</h4>
                      <p className="cart-drawer-item-price">
                        ₹{(Number(item.priceInr) || 0).toLocaleString()} each
                      </p>
                    </div>

                    <div className="cart-drawer-item-bottom">
                      <div className="cart-drawer-qty-controls">
                        <button
                          type="button"
                          className="cart-drawer-qty-btn"
                          onClick={() => handleUpdateQuantity(item, -1)}
                          disabled={loading}
                          aria-label="Decrease quantity"
                        >
                          <FiMinus aria-hidden="true" />
                        </button>
                        <span className="cart-drawer-qty-value">{item.quantity || 1}</span>
                        <button
                          type="button"
                          className="cart-drawer-qty-btn"
                          onClick={() => handleUpdateQuantity(item, 1)}
                          disabled={loading}
                          aria-label="Increase quantity"
                        >
                          <FiPlus aria-hidden="true" />
                        </button>
                      </div>

                      <div className="cart-drawer-item-subtotal">
                        <strong>₹{itemTotal.toLocaleString()}</strong>
                      </div>

                      <button
                        type="button"
                        className="cart-drawer-item-remove-btn"
                        onClick={() => handleRemove(item)}
                        disabled={loading}
                        aria-label={`Remove ${item.packageName || 'item'} from cart`}
                      >
                        <FiTrash2 aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-drawer-subtotal-row">
              <span className="cart-drawer-subtotal-label">Subtotal</span>
              <span className="cart-drawer-subtotal-value">
                ₹{totalAmount.toLocaleString()}
              </span>
            </div>
            <p className="cart-drawer-tax-note">Taxes & setup included. Fast online onboarding.</p>

            <button
              type="button"
              className="cart-drawer-checkout-btn"
              onClick={handleCheckout}
            >
              <span>Proceed to Checkout</span>
              <FiArrowRight aria-hidden="true" />
            </button>

            <button
              type="button"
              className="cart-drawer-clear-btn"
              onClick={handleClear}
              disabled={loading}
            >
              Clear Cart
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
